// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@anchorbase.com).

import { z } from 'zod';
import type { AnchorbaseLoginDeepLinkToken } from '../auth.types';
import { AnchorbaseLoginDeepLinkTokenSchema } from './auth.schemas';

export interface AnchorbaseLoginActionLink {
    readonly version: 1;
    readonly action: 'login';
    readonly login_kind: 'passwordless';
    readonly token: AnchorbaseLoginDeepLinkToken;
}

export interface AnchorbaseNavigateActionLink {
    readonly version: 1;
    readonly action: 'navigate';
    readonly url: string;
}

export interface AnchorbaseRpaActionLink {
    readonly version: 1;
    readonly action: 'rpa';
    readonly workflow_id: string;
    readonly website_system_id: number;
}

// Discriminator: action
export type AnchorbaseActionLink =
    | AnchorbaseLoginActionLink
    | AnchorbaseNavigateActionLink
    | AnchorbaseRpaActionLink;

const AnchorbaseLoginActionLinkSchema = z
    .object({
        version: z.literal(1),
        action: z.literal('login'),
        login_kind: z.literal('passwordless'),
        token: AnchorbaseLoginDeepLinkTokenSchema,
    })
    .strict() satisfies z.ZodType<AnchorbaseLoginActionLink>;

const AnchorbaseNavigateActionLinkSchema = z
    .object({
        version: z.literal(1),
        action: z.literal('navigate'),
        url: z.string().url(),
    })
    .strict() satisfies z.ZodType<AnchorbaseNavigateActionLink>;

const AnchorbaseRpaActionLinkSchema = z
    .object({
        version: z.literal(1),
        action: z.literal('rpa'),
        workflow_id: z.string().regex(/^[a-z][a-z0-9_]*$/),
        website_system_id: z.number().int().positive().safe(),
    })
    .strict() satisfies z.ZodType<AnchorbaseRpaActionLink>;

export const AnchorbaseActionLinkV1Schema = z.discriminatedUnion('action', [
    AnchorbaseLoginActionLinkSchema,
    AnchorbaseNavigateActionLinkSchema,
    AnchorbaseRpaActionLinkSchema,
]) satisfies z.ZodType<AnchorbaseActionLink>;

function requireQuery(
    url: URL,
    requiredNames: readonly string[],
    optionalNames: readonly string[] = []
): ReadonlyMap<string, string> {
    const allowedNames = new Set([...requiredNames, ...optionalNames]);
    const values = new Map<string, string>();
    for (const name of url.searchParams.keys()) {
        if (!allowedNames.has(name) || url.searchParams.getAll(name).length !== 1) {
            throw new Error(`Anchorbase action link has invalid query field: ${name}`);
        }
        const value = url.searchParams.get(name);
        if (value === null) {
            throw new Error(`Anchorbase action link is missing query field: ${name}`);
        }
        values.set(name, value);
    }
    for (const name of requiredNames) {
        if (!values.has(name)) {
            throw new Error(`Anchorbase action link is missing query field: ${name}`);
        }
    }
    return values;
}

function parseVersion(url: URL): 1 {
    const versions = url.searchParams.getAll('version');
    if (versions.length > 1 || (versions.length === 1 && versions[0] !== '1')) {
        throw new Error('Anchorbase action link version is unsupported');
    }
    return 1;
}

function requireQueryValue(query: ReadonlyMap<string, string>, name: string): string {
    const value = query.get(name);
    if (value === undefined) {
        throw new Error(`Anchorbase action link is missing query field: ${name}`);
    }
    return value;
}

export function parseAnchorbaseActionLinkV1(value: string): AnchorbaseActionLink {
    const url = new URL(value);
    if (
        url.protocol !== 'anchorbase:' ||
        url.username.length !== 0 ||
        url.password.length !== 0 ||
        url.port.length !== 0
    ) {
        throw new Error('Anchorbase action link URL is invalid');
    }
    const version = parseVersion(url);
    switch (url.hostname) {
        case 'login': {
            requireQuery(url, [], ['version']);
            if (url.pathname !== '/passwordless' || url.hash.length <= 1) {
                throw new Error('Anchorbase login action is invalid');
            }
            return AnchorbaseActionLinkV1Schema.parse({
                version,
                action: 'login',
                login_kind: 'passwordless',
                token: url.hash.slice(1),
            });
        }
        case 'navigate': {
            const query = requireQuery(url, ['url'], ['version']);
            if (url.pathname.length !== 0 || url.hash.length !== 0) {
                throw new Error('Anchorbase navigate action is invalid');
            }
            const destination = new URL(requireQueryValue(query, 'url'));
            if (
                !['http:', 'https:'].includes(destination.protocol) ||
                destination.username.length !== 0 ||
                destination.password.length !== 0
            ) {
                throw new Error('Anchorbase navigate destination is invalid');
            }
            return AnchorbaseActionLinkV1Schema.parse({
                version,
                action: 'navigate',
                url: destination.toString(),
            });
        }
        case 'rpa': {
            const query = requireQuery(url, ['website_system_id'], ['version']);
            if (url.hash.length !== 0) {
                throw new Error('Anchorbase RPA action is invalid');
            }
            return AnchorbaseActionLinkV1Schema.parse({
                version,
                action: 'rpa',
                workflow_id: url.pathname.slice(1),
                website_system_id: Number(requireQueryValue(query, 'website_system_id')),
            });
        }
        default:
            throw new Error('Anchorbase action is unsupported');
    }
}

export function writeAnchorbaseActionLinkV1(actionLink: AnchorbaseActionLink): string {
    const parsedActionLink = AnchorbaseActionLinkV1Schema.parse(actionLink);
    switch (parsedActionLink.action) {
        case 'login':
            return `anchorbase://login/passwordless#${parsedActionLink.token.token}`;
        case 'navigate': {
            const url = new URL('anchorbase://navigate');
            url.searchParams.set('url', parsedActionLink.url);
            return url.toString();
        }
        case 'rpa': {
            const url = new URL(`anchorbase://rpa/${parsedActionLink.workflow_id}`);
            url.searchParams.set(
                'website_system_id',
                parsedActionLink.website_system_id.toString()
            );
            return url.toString();
        }
    }
}

export const AnchorbaseActionLinkContract = {
    variants: {
        v1: { normalize: parseAnchorbaseActionLinkV1 },
    },
    writerVariant: 'v1',
} as const;
